import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Info } from 'lucide-react';

/**
 * KPIDefinitionModal — lightweight modal that explains how a KPI is calculated.
 * Uses the KPI's `definition` object:
 *   {
 *     title: string,
 *     value: string,           // formatted current value
 *     formula: string,         // e.g., "Deal Value × 5%"
 *     description: string,     // plain-language explanation
 *     inputs?: [{ label, value }],  // input breakdown (optional)
 *     source?: string,         // where the data comes from
 *   }
 */
export const KPIDefinitionModal = ({ open, onOpenChange, definition }) => {
  if (!definition) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="kpi-definition-modal" aria-describedby="kpi-def-body">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600"><Info className="h-4 w-4" /></div>
            <span>{definition.title}</span>
          </DialogTitle>
        </DialogHeader>
        <div id="kpi-def-body" className="space-y-4 text-sm">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Current Value</p>
            <p className="text-2xl font-bold text-slate-900">{definition.value}</p>
          </div>
          {definition.formula && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Formula</p>
              <code className="block bg-slate-50 text-slate-800 p-3 rounded-lg text-xs font-mono">{definition.formula}</code>
            </div>
          )}
          {definition.description && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">How it's computed</p>
              <p className="text-sm text-slate-700 leading-relaxed">{definition.description}</p>
            </div>
          )}
          {Array.isArray(definition.inputs) && definition.inputs.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Breakdown</p>
              <div className="space-y-1.5">
                {definition.inputs.map((inp, i) => (
                  <div key={`${inp.label || 'input'}-${i}`} className="flex justify-between items-center py-1.5 px-2 bg-slate-50 rounded">
                    <span className="text-xs text-slate-600">{inp.label}</span>
                    <span className="text-xs font-semibold text-slate-900 tabular-nums">{inp.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {definition.source && (
            <div className="pt-2 border-t">
              <p className="text-xs text-slate-400">Source: {definition.source}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Hook to manage KPI definition modal state from any component.
 * Usage:
 *   const { openKPI, modal } = useKPIDefinition();
 *   ...
 *   <DashKPI onClick={() => openKPI({ title: 'Total Leads', ... })} />
 *   {modal}
 */
export const useKPIDefinition = () => {
  const [open, setOpen] = useState(false);
  const [def, setDef] = useState(null);
  const openKPI = (definition) => {
    setDef(definition);
    setOpen(true);
  };
  const modal = <KPIDefinitionModal open={open} onOpenChange={setOpen} definition={def} />;
  return { openKPI, modal };
};
